import { alpha, Box, Chip, Collapse, IconButton, ListItemButton, Stack, Tooltip, Typography } from "@mui/material";
import { CloseOutlined, OpenInNewOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { FONT_WEIGHTS } from "Theme/Typography/Constants";
import { CUSTOM_RADII } from "Theme/Shape";
import type { INotification } from "Common/Interfaces";

interface INotificationRowProps {
  notification: INotification;
  onNotificationClick: (notification: INotification, openDetail?: boolean) => void;
  onDismiss: (e: React.MouseEvent, notificationId: string) => void;
  formatRelativeTime: (isoString: string) => string;
  isNew: boolean;
  isExiting: boolean;
  onExited: () => void;
}

export const NotificationRow = (props: INotificationRowProps) => {
  const { notification, onNotificationClick, onDismiss, formatRelativeTime, isNew, isExiting, onExited } = props;
  const theme = useTheme();

  return (
    <Collapse in={!isExiting} appear={isNew} onExited={onExited} timeout={{ appear: 300, enter: 300, exit: 200 }}>
      <ListItemButton
        onClick={() => onNotificationClick(notification)}
        sx={{
          alignItems: "flex-start",
          pl: 0,
          pr: theme.spacing(1.5),
          py: theme.spacing(1.5),
          opacity: isExiting ? 0 : 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: isNew ? alpha(theme.palette.primary.main, 0.1) : "transparent",
          transform: isNew ? "translateY(-2px)" : "translateY(0)",
          transition: ["background-color 1500ms ease-out", "opacity 200ms ease-out", "transform 300ms ease-out"].join(", "),
          animation: isNew ? "notificationArrivalPulse 600ms ease-out" : "none",
          "@keyframes notificationArrivalPulse": {
            "0%": {
              backgroundColor: alpha(theme.palette.primary.main, 0.25),
            },
            "100%": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
          },
          "&:hover": {
            backgroundColor: isNew ? alpha(theme.palette.primary.main, 0.16) : theme.palette.action.hover,
          },
        }}
      >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start", width: "100%" }}>
        {/* Unread indicator */}
        <Box
          sx={{
            alignSelf: "center",
            minWidth: 8,
            width: 8,
            height: 8,
            borderRadius: CUSTOM_RADII.circle,
            backgroundColor: notification.isRead ? "transparent" : theme.palette.primary.main,
            flexShrink: 0,
          }}
        />

        {/* Content */}
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          {/* Application chip + navigation icon */}
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Chip
              label={notification.applicationName}
              size="small"
              sx={(theme) => ({
                height: 20,
                fontSize: "0.6rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[300]}`,
              })}
            />
            {notification.deepLink && (
              <Tooltip title="Open record in new tab" placement="top" arrow>
                <IconButton
                  size="small"
                  href={notification.deepLink}
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNotificationClick(notification, false);
                  }}
                  sx={{
                    padding: 0.25,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      color: theme.palette.primary.dark,
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                  aria-label="Open record in new tab"
                >
                  <OpenInNewOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Title */}
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.4,
              fontWeight: notification.isRead ? FONT_WEIGHTS.REGULAR : FONT_WEIGHTS.SEMI_BOLD,
            }}
          >
            {notification.title}
          </Typography>

          {/* Message preview */}
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }} noWrap>
            {notification.message}
          </Typography>
        </Stack>

        {/* Timestamp + Dismiss */}
        <Stack spacing={0.5} sx={{ alignItems: "flex-end", flexShrink: 0, minWidth: 56 }}>
          <Typography variant="caption" sx={{ whiteSpace: "nowrap", color: theme.palette.text.disabled }}>
            {formatRelativeTime(notification.createdAt)}
          </Typography>
          <Tooltip title="Dismiss" placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => onDismiss(e, notification.id)}
              sx={{
                padding: 0.5,
                color: theme.palette.text.disabled,
                "&:hover": {
                  color: theme.palette.text.primary,
                },
              }}
              aria-label="dismiss notification"
            >
              <CloseOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      </ListItemButton>
    </Collapse>
  );
};
